<template>
  <div class="admin-page">
    <div class="admin-head">
      <div class="head-left">
        <h2 class="title">회원 관리</h2>
        <div class="chips">
          <span class="chip">전체 {{ members.length }}명</span>
          <span class="chip admin">관리자 {{ adminCount }}명</span>
        </div>
      </div>
      <div class="head-right">
        <!-- 실명은 기본으로 가려 둔다. 관리 화면을 켜 둔 것만으로 노출되면 안 된다. -->
        <button class="btn-eye" :class="{ on: revealNames }"
                :title="revealNames ? '이름 가리기' : '이름 보기'"
                @click="revealNames = !revealNames">
          {{ revealNames ? "🙈" : "👁" }}
          <span>{{ revealNames ? "이름 가리기" : "이름 보기" }}</span>
        </button>
        <input
          v-model="keyword"
          class="search"
          type="text"
          placeholder="아이디 · 닉네임 검색"
        />
      </div>
    </div>

    <div class="message" v-if="message">{{ message }}</div>

    <!-- 데스크톱: 테이블 -->
    <div class="table-scroll" v-if="filtered.length > 0">
      <table class="member-table">
        <thead>
          <tr>
            <th class="col-id" @click="sortBy('id')">아이디 <span class="arrow">{{ arrow('id') }}</span></th>
            <th>이름</th>
            <th>닉네임</th>
            <th>역할</th>
            <th class="col-date" @click="sortBy('createDatetime')">가입일 <span class="arrow">{{ arrow('createDatetime') }}</span></th>
            <th class="col-date" @click="sortBy('lastLoginDatetime')">마지막 로그인 <span class="arrow">{{ arrow('lastLoginDatetime') }}</span></th>
            <th class="col-date" @click="sortBy('lastAccessDatetime')">마지막 접근 <span class="arrow">{{ arrow('lastAccessDatetime') }}</span></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="m in filtered" :key="m.userId">
            <td class="mono">{{ m.id }}</td>
            <td class="col-name" :class="{ masked: !revealNames }">{{ showName(m) }}</td>
            <td>{{ m.nickname }}</td>
            <td>
              <span class="role-badge" :class="{ admin: isAdmin(m) }">
                {{ isAdmin(m) ? "관리자" : "일반" }}
              </span>
            </td>
            <td>
              <div class="date-main">{{ fmtDate(m.createDatetime) }}</div>
            </td>
            <td>
              <div class="date-main">{{ fmtDate(m.lastLoginDatetime) }}</div>
              <div class="date-rel" v-if="m.lastLoginDatetime">{{ relTime(m.lastLoginDatetime) }}</div>
            </td>
            <td>
              <div class="date-main">{{ fmtDate(m.lastAccessDatetime) }}</div>
              <div class="date-rel" v-if="m.lastAccessDatetime">{{ relTime(m.lastAccessDatetime) }}</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 모바일: 카드 -->
    <div class="card-list" v-if="filtered.length > 0">
      <div class="member-card" v-for="m in filtered" :key="m.userId">
        <div class="card-top">
          <div class="card-id">
            <span class="mono">{{ m.id }}</span>
            <span class="nick">{{ m.nickname }}</span>
            <span class="card-name" :class="{ masked: !revealNames }">{{ showName(m) }}</span>
          </div>
          <span class="role-badge" :class="{ admin: isAdmin(m) }">
            {{ isAdmin(m) ? "관리자" : "일반" }}
          </span>
        </div>
        <dl class="card-dates">
          <div><dt>가입</dt><dd>{{ fmtDate(m.createDatetime) }}</dd></div>
          <div><dt>로그인</dt><dd>{{ fmtDate(m.lastLoginDatetime) }} <em v-if="m.lastLoginDatetime">({{ relTime(m.lastLoginDatetime) }})</em></dd></div>
          <div><dt>접근</dt><dd>{{ fmtDate(m.lastAccessDatetime) }} <em v-if="m.lastAccessDatetime">({{ relTime(m.lastAccessDatetime) }})</em></dd></div>
        </dl>
      </div>
    </div>

    <div class="empty" v-if="!message && filtered.length === 0">
      표시할 회원이 없습니다.
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import UtilMixin from "@/common/mixins/UtilMixin";

interface Member {
  userId: number;
  id: string;
  name: string | null;
  nickname: string;
  role: string;
  permissions: string[];
  createDatetime: string | null;
  lastLoginDatetime: string | null;
  lastAccessDatetime: string | null;
}

export default defineComponent({
  name: "MemberManageView",
  mixins: [UtilMixin],
  data() {
    return {
      members: [] as Member[],
      keyword: "",
      message: "",
      sortKey: "lastAccessDatetime" as keyof Member,
      sortAsc: false,
      // 실명은 기본으로 가린다. 눈 버튼을 누른 동안에만 보인다(새로고침하면 다시 가려짐).
      revealNames: false,
    };
  },
  computed: {
    adminCount(): number {
      return this.members.filter((m) => this.isAdmin(m)).length;
    },
    filtered(): Member[] {
      const kw = this.keyword.trim().toLowerCase();
      let list = this.members;
      if (kw) {
        list = list.filter(
          (m) =>
            (m.id || "").toLowerCase().includes(kw) ||
            (m.nickname || "").toLowerCase().includes(kw)
        );
      }
      const key = this.sortKey;
      const dir = this.sortAsc ? 1 : -1;
      return [...list].sort((a, b) => {
        // ISO 날짜/아이디 모두 문자열 사전식 비교로 안전하게(날짜는 사전식=시간순)
        const av = String(a[key] ?? "");
        const bv = String(b[key] ?? "");
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    },
  },
  methods: {
    isAdmin(m: Member): boolean {
      return m.role === "ROLE_ADMIN";
    },
    /**
     * 이름 표시. 가림 상태에서는 성만 남기고 나머지를 ○ 로 바꾼다.
     * (홍길동 → 홍○○) 글자 수는 남겨야 동명이인을 구분할 수 있다.
     */
    showName(m: Member): string {
      const name = (m.name || "").trim();
      if (!name) return "-";
      if (this.revealNames) return name;
      if (name.length === 1) return "○";
      return name.charAt(0) + "○".repeat(name.length - 1);
    },
    sortBy(key: keyof Member) {
      if (this.sortKey === key) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortKey = key;
        this.sortAsc = false;
      }
    },
    arrow(key: keyof Member): string {
      if (this.sortKey !== key) return "";
      return this.sortAsc ? "▲" : "▼";
    },
    fmtDate(iso: string | null): string {
      if (!iso) return "-";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "-";
      const p = (n: number) => String(n).padStart(2, "0");
      return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(
        d.getHours()
      )}:${p(d.getMinutes())}`;
    },
    relTime(iso: string | null): string {
      if (!iso) return "";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "";
      const diff = Date.now() - d.getTime();
      const m = Math.floor(diff / 60000);
      if (m < 1) return "방금";
      if (m < 60) return `${m}분 전`;
      const h = Math.floor(m / 60);
      if (h < 24) return `${h}시간 전`;
      const days = Math.floor(h / 24);
      if (days < 30) return `${days}일 전`;
      const mon = Math.floor(days / 30);
      if (mon < 12) return `${mon}개월 전`;
      return `${Math.floor(mon / 12)}년 전`;
    },
    async loadMembers() {
      this.message = "";
      try {
        const res = await this.getApi().user().getUserPermissions();
        if (Array.isArray(res)) {
          this.members = res as Member[];
        } else {
          this.message = "회원 목록 조회 실패 (관리자 계정으로 로그인했는지 확인)";
        }
      } catch (e) {
        this.message = "회원 목록 조회 실패 (관리자 계정으로 로그인했는지 확인)";
      }
    },
  },
  created() {
    this.loadMembers();
  },
});
</script>

<style lang="scss" scoped>
.admin-page {
  padding: 20px;

  .admin-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;

    .head-left {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;

      .title {
        font-size: 1.4rem;
        font-weight: 700;
        margin: 0;
      }
      .chips {
        display: flex;
        gap: 6px;
        .chip {
          font-size: 0.8rem;
          padding: 3px 10px;
          border-radius: 999px;
          background: rgba(128, 128, 128, 0.15);
          &.admin {
            background: rgba(46, 125, 50, 0.15);
            color: #2e7d32;
          }
        }
      }
    }

    .head-right {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    .search {
      min-width: 180px;
      padding: 8px 12px;
      border: 1px solid rgba(128, 128, 128, 0.35);
      border-radius: 8px;
      background: var(--t-bg-input, #fff);
      color: inherit;
      font-size: 0.9rem;
    }

    // 실명 보기 토글. 켜져 있는 동안 티가 나야 한다 — 켜 둔 줄 모르고 화면을 띄워 두면 곤란하다.
    .btn-eye {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 12px;
      border: 1px solid rgba(128, 128, 128, 0.35);
      border-radius: 8px;
      background: none;
      color: inherit;
      font-size: 0.85rem;
      cursor: pointer;
      white-space: nowrap;

      &:hover { border-color: rgba(128, 128, 128, 0.6); }
      &.on {
        background: rgba(217, 83, 79, 0.14);
        border-color: rgba(217, 83, 79, 0.5);
        color: #d9534f;
        font-weight: 700;
      }
      @media (max-width: 560px) { span { display: none; } }
    }
  }

  .col-name,
  .card-name {
    &.masked {
      letter-spacing: 0.08em;
      opacity: 0.75;
    }
  }
  .card-name {
    font-size: 0.85rem;
    opacity: 0.8;
  }

  .message {
    margin-bottom: 12px;
    color: #d9534f;
  }
  .empty {
    padding: 40px 0;
    text-align: center;
    opacity: 0.6;
  }

  .mono {
    font-family: "Menlo", "Consolas", monospace;
    font-weight: 600;
  }
  .role-badge {
    font-size: 0.78rem;
    padding: 2px 9px;
    border-radius: 999px;
    background: rgba(128, 128, 128, 0.18);
    white-space: nowrap;
    &.admin {
      background: rgba(46, 125, 50, 0.18);
      color: #2e7d32;
      font-weight: 700;
    }
  }
  .date-main {
    white-space: nowrap;
  }
  .date-rel {
    font-size: 0.75rem;
    opacity: 0.55;
  }

  /* 데스크톱 테이블 */
  .table-scroll {
    overflow-x: auto;
    display: block;
  }
  .member-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.92rem;

    th,
    td {
      border-bottom: 1px solid rgba(128, 128, 128, 0.2);
      padding: 10px 12px;
      text-align: left;
      vertical-align: middle;
    }
    thead th {
      font-weight: 600;
      background: rgba(128, 128, 128, 0.06);
      white-space: nowrap;
      user-select: none;
      &.col-date,
      &.col-id {
        cursor: pointer;
      }
      .arrow {
        font-size: 0.7rem;
        opacity: 0.7;
      }
    }
    tbody tr:hover {
      background: rgba(128, 128, 128, 0.06);
    }
  }

  /* 모바일 카드 */
  .card-list {
    display: none;
    flex-direction: column;
    gap: 10px;

    .member-card {
      border: 1px solid rgba(128, 128, 128, 0.25);
      border-radius: 10px;
      padding: 12px 14px;

      .card-top {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
        .card-id {
          display: flex;
          flex-direction: column;
          .nick {
            font-size: 0.82rem;
            opacity: 0.7;
          }
        }
      }
      .card-dates {
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 4px;
        div {
          display: flex;
          gap: 8px;
          font-size: 0.85rem;
        }
        dt {
          width: 44px;
          opacity: 0.6;
          flex: 0 0 auto;
        }
        dd {
          margin: 0;
          em {
            font-style: normal;
            opacity: 0.55;
            font-size: 0.78rem;
          }
        }
      }
    }
  }

  @media (max-width: 700px) {
    .table-scroll {
      display: none;
    }
    .card-list {
      display: flex;
    }
    .admin-head {
      .search {
        width: 100%;
      }
    }
  }
}
</style>
